import { useState, useRef, useEffect, useContext, useCallback } from 'react';
import {
  FiClock, FiCalendar, FiCheckCircle, FiStar, FiUser, FiAward,
  FiInfo, FiChevronDown, FiChevronUp, FiX, FiEdit2, FiTrash2
} from 'react-icons/fi';
import {
  FaWhatsapp, FaRegComment, FaThumbsUp, FaRegThumbsUp,
  FaEnvelope, FaRegCalendarAlt, FaExclamationTriangle, FaRedo, FaSpinner,
  FaUserFriends
} from 'react-icons/fa';
import { RiMedalLine } from 'react-icons/ri';
import { toast } from 'react-toastify';
import HttpInterceptor from '../../lib/HttpInterceptor';
import { useNavigate } from 'react-router-dom';
import Avatar from '../../Components/Avatar';
import StarRating from '../../Components/StarRating';
import UserContext from '../../Components/UserContext';
import { formatName } from '../../utils/dataTransformers';
import ConfirmDialog from '../../Components/ConfirmDialog';

function MyTrainer() {
  const navigate = useNavigate();
  const { session } = useContext(UserContext);
  const [isLoading, setIsLoading] = useState(true);
  const [enrolledTrainers, setEnrolledTrainers] = useState([]);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 3;
  const [selectedTrainer, setSelectedTrainer] = useState(null);

  // Fetch enrolled trainers from backend
  const fetchTrainers = useCallback(async (isRetry = false) => {
    try {
      setError(null);
      setIsLoading(true);
      const response = await HttpInterceptor.get('/api/students/trainers');
      if (response.status === 200) {
        // Handle both array and object responses
        const trainersData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
        // Set liked status for reviews based on current user
        const userId = session?.id || session?.userId;
        const trainersWithLikes = trainersData.map(trainer => ({
          ...trainer,
          reviews: trainer.reviews?.map(review => {
            // Ensure likes is an array before calling .some()
            const likesArray = Array.isArray(review.likes) ? review.likes : [];
            return {
              ...review,
              liked: likesArray.some(likeId =>
                likeId?.toString() === userId?.toString()
              ) || false
            };
          }) || []
        }));
        setEnrolledTrainers(trainersWithLikes);

        // Update selectedTrainer if it exists to reflect updated reviews
        setSelectedTrainer(prevSelected => {
          if (prevSelected) {
            const updatedSelectedTrainer = trainersWithLikes.find(t => t.id === prevSelected.id);
            return updatedSelectedTrainer || prevSelected;
          }
          return prevSelected;
        });
      } else {
        // Handle non-200 status codes
        setError({
          message: 'Failed to load trainers. Please try again.',
          retryable: true
        });
        setEnrolledTrainers([]);
      }
    } catch (err) {
      console.error('Error fetching trainers:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load trainers';

      if (retryCount < MAX_RETRIES && !isRetry) {
        setError({
          message: errorMessage,
          retryable: true
        });
        toast.error('Failed to load trainers. Please try again.');
      } else {
        setError({
          message: 'Unable to load trainers. Please check your connection and try again later.',
          retryable: false
        });
        toast.error('Unable to load trainers');
      }
      setEnrolledTrainers([]);
    } finally {
      setIsLoading(false);
    }
  }, [retryCount, session?.id, session?.userId]);

  useEffect(() => {
    fetchTrainers();
  }, [fetchTrainers]);

  // Detect PWA mode
  useEffect(() => {
    const checkPWA = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true;
      setIsPWA(standalone);
    };
    checkPWA();
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleChange = () => checkPWA();
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Fetch schedules and next sessions for enrolled trainers
  useEffect(() => {
    const fetchSchedules = async () => {
      if (enrolledTrainers.length === 0) return;

      const schedules = {};
      const nextSessionsData = {};

      for (const trainer of enrolledTrainers) {
        for (const program of trainer.programs || []) {
          try {
            // Fetch schedule
            const scheduleResponse = await HttpInterceptor.get('/api/classes/schedule', {
              params: {
                programId: program.id || program._id,
                studentId: null // Current user
              }
            });
            if (scheduleResponse.status === 200) {
              schedules[program.id || program._id] = scheduleResponse.data;
            }

            // Fetch next session
            const nextSessionResponse = await HttpInterceptor.get('/api/classes/next-session', {
              params: {
                programId: program.id || program._id,
                studentId: null // Current user
              }
            });
            if (nextSessionResponse.status === 200 && nextSessionResponse.data.nextSession) {
              nextSessionsData[program.id || program._id] = nextSessionResponse.data.nextSession;
            }
          } catch (error) {
            console.error('Error fetching schedule:', error);
          }
        }
      }

      setTrainerSchedules(schedules);
      setNextSessions(nextSessionsData);
    };

    if (enrolledTrainers.length > 0) {
      fetchSchedules();
    }
  }, [enrolledTrainers]);

  // Fetch student messages
  useEffect(() => {
    const fetchMessages = async () => {
      try {
        setIsLoadingMessages(true);
        const response = await HttpInterceptor.get('/api/messages/student');
        if (response.status === 200) {
          setStudentMessages(response.data?.data || []);
        }
      } catch (error) {
        console.error('Error fetching messages:', error);
      } finally {
        setIsLoadingMessages(false);
      }
    };

    fetchMessages();
  }, []);

  // Load message thread
  const loadMessageThread = async (threadId) => {
    if (showMessageThread === threadId) {
      setShowMessageThread(null);
      setMessageThread([]);
      return;
    }

    try {
      setIsLoadingMessages(true);
      const response = await HttpInterceptor.get(`/api/messages/thread/${threadId}`);
      if (response.status === 200) {
        setMessageThread(response.data?.data || []);
        setShowMessageThread(threadId);
      }
    } catch (error) {
      console.error('Error loading thread:', error);
      toast.error('Failed to load conversation');
    } finally {
      setIsLoadingMessages(false);
    }
  };

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    fetchTrainers(true);
  };

  // State management
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [currentReview, setCurrentReview] = useState({
    rating: 5,
    comment: '',
    trainerId: null,
    programId: null,
    planId: null
  });
  const [messageContent, setMessageContent] = useState('');
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [trainerSchedules, setTrainerSchedules] = useState({});
  const [nextSessions, setNextSessions] = useState({});
  const [studentMessages, setStudentMessages] = useState([]);
  const [showMessageThread, setShowMessageThread] = useState(null);
  const [messageThread, setMessageThread] = useState([]);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isPWA, setIsPWA] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => { },
    variant: 'primary'
  });

  const profileRef = useRef(null);
  const messageInputRef = useRef(null);

  // Scroll to profile when selected
  useEffect(() => {
    if (selectedTrainer && profileRef.current) {
      profileRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [selectedTrainer]);

  // Focus message input when modal opens
  useEffect(() => {
    if (showMessageModal && messageInputRef.current) {
      messageInputRef.current.focus();
    }
  }, [showMessageModal]);

  // Handle like review
  const handleLikeReview = async (trainerId, reviewId) => {
    try {
      // reviewId is the planId
      const response = await HttpInterceptor.post(`/api/students/review/${reviewId}/like`);

      if (response.status === 200) {
        const { isLiked, likesCount } = response.data?.data || {};
        // Update local state
        setEnrolledTrainers(prevTrainers =>
          prevTrainers.map(trainer => {
            if (trainer.id === trainerId) {
              const updatedReviews = trainer.reviews.map(review => {
                if (review.id === reviewId) {
                  return {
                    ...review,
                    liked: isLiked,
                    likes: likesCount || 0
                  };
                }
                return review;
              });
              return { ...trainer, reviews: updatedReviews };
            }
            return trainer;
          })
        );

        if (selectedTrainer && selectedTrainer.id === trainerId) {
          const updatedTrainer = enrolledTrainers.find(t => t.id === trainerId);
          if (updatedTrainer) {
            const updatedReviews = updatedTrainer.reviews.map(review => {
              if (review.id === reviewId) {
                return {
                  ...review,
                  liked: isLiked,
                  likes: likesCount || 0
                };
              }
              return review;
            });
            setSelectedTrainer({ ...updatedTrainer, reviews: updatedReviews });
          }
        }
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      const errorMessage = error.response?.data?.message || 'Failed to like/unlike review';
      toast.error(errorMessage);
    }
  };

  // Handle edit review
  const handleEditReview = (review) => {
    // Find the program that has this review to get planId
    const programWithReview = selectedTrainer?.programs?.find(p => {
      // The review.id should match the student enrollment _id (planId)
      return true; // We'll use the first program or find by matching
    });

    setEditingReview(review.id);
    setCurrentReview({
      rating: review.rating,
      comment: review.comment,
      trainerId: selectedTrainer?.id,
      programId: programWithReview?.id || programWithReview?._id || null,
      planId: review.id || programWithReview?.planId || null // review.id is the planId
    });
    setShowReviewModal(true);
  };

  // Handle delete review
  const handleDeleteReview = (trainerId, reviewId) => {
    setConfirmConfig({
      isOpen: true,
      title: 'Delete Review',
      message: 'Are you sure you want to delete this review? This action cannot be undone.',
      variant: 'danger',
      onConfirm: async () => {
        try {
          // reviewId is the planId
          const response = await HttpInterceptor.delete(`/api/students/review/${reviewId}`);

          if (response.status === 200) {
            toast.success('Review deleted successfully');
            // Refresh trainers data to get updated reviews
            await fetchTrainers(true);
          }
        } catch (error) {
          console.error('Error deleting review:', error);
          const errorMessage = error.response?.data?.message || 'Failed to delete review';
          toast.error(errorMessage);
        }
      }
    });
  };

  // Handle review submission
  const handleReviewSubmit = async () => {
    if (!currentReview.rating) {
      toast.error('Please select a rating');
      return;
    }

    if (!currentReview.planId) {
      toast.error('Program information is missing');
      return;
    }

    try {
      if (editingReview) {
        // Update existing review
        const response = await HttpInterceptor.patch(
          `/api/students/review/${currentReview.planId}`,
          {
            rating: currentReview.rating,
            text: currentReview.comment,
            visible: true
          }
        );

        if (response.status === 200) {
          toast.success('Review updated successfully');
          // Refresh trainers data to get updated reviews
          await fetchTrainers(true);
        }
      } else {
        // Submit new review
        const response = await HttpInterceptor.post(
          `/api/students/review/${currentReview.planId}`,
          {
            rating: currentReview.rating,
            text: currentReview.comment,
            visible: true
          }
        );

        if (response.status === 200) {
          toast.success('Thank you for your review!');
          // Refresh trainers data to get updated reviews
          await fetchTrainers(true);

          // Also refresh the selected trainer's reviews if modal is open
          if (selectedTrainer) {
            const updatedTrainer = enrolledTrainers.find(t => t.id === selectedTrainer.id);
            if (updatedTrainer) {
              setSelectedTrainer(updatedTrainer);
            }
          }
        }
      }

      setShowReviewModal(false);
      setCurrentReview({ rating: 5, comment: '', trainerId: null, programId: null, planId: null });
      setEditingReview(null);
    } catch (error) {
      console.error('Error submitting review:', error);
      const errorMessage = error.response?.data?.message || 'Failed to submit review';
      toast.error(errorMessage);
    }
  };

  // Handle send message
  const handleSendMessage = async () => {
    if (!messageContent.trim()) {
      toast.error('Message cannot be empty');
      return;
    }

    if (!selectedTrainer) {
      toast.error('No trainer selected');
      return;
    }

    try {
      // Get the program ID from the first program (if available)
      const programId = selectedTrainer.programs?.[0]?.id || null;

      const response = await HttpInterceptor.post('/api/messages/send', {
        trainerId: selectedTrainer.id,
        content: messageContent.trim(),
        type: 'message',
        programId: programId,
      });

      if (response.status === 201) {
        toast.success(`Message sent to ${formatName(selectedTrainer.name)}`);
        setShowMessageModal(false);
        setMessageContent('');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = error.response?.data?.message || 'Failed to send message. Please try again.';
      toast.error(errorMessage);
    }
  };

  // Render star rating - kept for backward compatibility but using StarRating component
  const renderStars = (rating, interactive = false, size = 'md') => {
    return <StarRating rating={rating || 0} size={size} />;
  };

  // Format date for display
  const formatDate = (dateString) => {
    const options = { weekday: 'short', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  return (
    <div className={`min-h-screen ${isPWA ? 'bg-gradient-to-b from-teal-50/40 via-blue-50/30 to-white' : 'bg-gradient-to-br from-gray-50 to-gray-100'}`}>
      {/* Hero Section */}
      <div className={`relative ${isPWA ? 'bg-gradient-to-br from-teal-50 via-blue-50 to-indigo-50' : ''} ${isPWA ? 'text-gray-900' : ''}`}>
        {isPWA && (
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-400 via-blue-400 to-indigo-400"></div>
        )}
        <div className={`relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${isPWA ? 'py-8 sm:py-10 pb-12 sm:pb-14' : 'py-12'}`}>
          <div className="text-center">
            <h1 className={`${isPWA ? 'text-xl sm:text-2xl' : 'text-4xl'} font-bold ${isPWA ? 'text-teal-800' : 'text-gray-900'} ${isPWA ? 'mb-3' : 'mb-2'} ${!isPWA ? 'font-serif' : ''}`}>
              My Mentors
            </h1>
            <p className={`${isPWA ? 'text-sm sm:text-base text-teal-600' : 'text-lg text-gray-600'}`}>
              Your current Mentors and programs
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Enrolled Trainers Section */}
        <div className={`${isPWA ? 'mb-8' : 'mb-16'}`}>
          <h2 className={`${isPWA ? 'text-lg sm:text-xl' : 'text-2xl'} font-semibold ${isPWA ? 'text-teal-800' : 'text-gray-900'} ${isPWA ? 'mb-4' : 'mb-6'}`}>
            My Mentors
          </h2>

          {/* Loading State - Only show if no data and no error */}
          {isLoading && enrolledTrainers.length === 0 && !error && (
            <div className={`text-center ${isPWA ? 'py-8 sm:py-10' : 'py-12'}`}>
              <FaSpinner className={`${isPWA ? 'text-5xl sm:text-6xl' : 'text-6xl'} mx-auto ${isPWA ? 'text-teal-500' : 'text-blue-600'} animate-spin mb-4`} />
              <p className={isPWA ? 'text-sm sm:text-base text-teal-600' : 'text-gray-600'}>Loading your mentors...</p>
            </div>
          )}

          {/* Error State */}
          {error && !isLoading && (
            <div className={`text-center ${isPWA ? 'py-8 sm:py-10' : 'py-12'}`}>
              <div className={`${isPWA ? 'text-red-400' : 'text-red-400'} mb-4`}>
                <FaExclamationTriangle className={`${isPWA ? 'text-5xl sm:text-6xl' : 'text-6xl'} mx-auto`} />
              </div>
              <h3 className={`${isPWA ? 'text-base sm:text-lg' : 'text-lg'} font-medium ${isPWA ? 'text-teal-800' : 'text-gray-900'} mb-2`}>
                Unable to Load Mentors
              </h3>
              <p className={`${isPWA ? 'text-sm sm:text-base text-teal-600' : 'text-gray-500'} mb-6`}>{error.message}</p>
              {error.retryable && (
                <div className={`flex justify-center ${isPWA ? 'gap-3' : 'gap-4'} ${isPWA ? 'flex-wrap' : ''}`}>
                  <button
                    onClick={handleRetry}
                    disabled={isLoading}
                    className={`${isPWA ? 'bg-gradient-to-r from-teal-500 to-blue-500 text-white shadow-lg ring-2 ring-teal-300/50 hover:from-teal-600 hover:to-blue-600' : 'bg-blue-600 text-white hover:bg-blue-700'} px-6 py-3 rounded-lg transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${isPWA ? 'font-semibold' : ''}`}
                  >
                    <FaRedo className={isLoading ? 'animate-spin' : ''} />
                    Retry
                  </button>
                  <button
                    onClick={() => navigate('/student/services')}
                    className={`${isPWA ? 'bg-white/95 backdrop-blur-sm border-2 border-teal-300/50 text-teal-700 hover:bg-teal-50 shadow-lg' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'} px-6 py-3 rounded-lg transition-colors ${isPWA ? 'font-semibold' : ''}`}
                  >
                    Find Mentors
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Empty State */}
          {!error && !isLoading && enrolledTrainers.length === 0 && (
            <div className={`text-center ${isPWA ? 'py-8 sm:py-10' : 'py-12'} ${isPWA ? 'bg-white/95 backdrop-blur-sm border-2 border-teal-200/50 shadow-xl' : 'bg-white rounded-xl shadow-sm'} p-8 rounded-xl`}>
              <div className={`${isPWA ? 'text-teal-400' : 'text-gray-400'} mb-4`}>
                <FaUserFriends className={`${isPWA ? 'text-5xl sm:text-6xl' : 'text-6xl'} mx-auto`} />
              </div>
              <h3 className={`${isPWA ? 'text-base sm:text-lg' : 'text-lg'} font-medium ${isPWA ? 'text-teal-800' : 'text-gray-900'} mb-2`}>
                No Mentors Enrolled
              </h3>
              <p className={`${isPWA ? 'text-sm sm:text-base text-teal-600' : 'text-gray-500'} mb-6`}>
                You're not enrolled with any mentors yet. Start your journey by finding the perfect mentor for you.
              </p>
              <button
                onClick={() => navigate('/student/services')}
                className={`${isPWA ? 'bg-gradient-to-r from-teal-500 to-blue-500 text-white shadow-lg ring-2 ring-teal-300/50 hover:from-teal-600 hover:to-blue-600' : 'bg-blue-600 text-white hover:bg-blue-700'} px-6 py-3 rounded-lg transition-all ${isPWA ? 'font-semibold' : ''}`}
              >
                Find Mentors
              </button>
            </div>
          )}

          {/* Trainers Grid */}
          {!error && !isLoading && enrolledTrainers.length > 0 && (
            <div className={`grid ${isPWA ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'} ${isPWA ? 'gap-4' : 'gap-6'}`}>
              {enrolledTrainers.map((trainer) => (
                <div
                  key={trainer.id}
                  className={`${isPWA ? 'bg-white/95 backdrop-blur-sm border-2 border-teal-200/50 shadow-xl hover:shadow-2xl hover:border-teal-300/70' : 'bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md'} rounded-xl overflow-hidden transition-all duration-300`}
                  aria-labelledby={`trainer-${trainer.id}-title`}
                >
                  <div className="p-6">
                    <div className="flex items-start space-x-4 mb-4">
                      <Avatar
                        src={(trainer.image && trainer.image.trim()) || trainer.profileImage}
                        name={trainer.name || 'Trainer'}
                        size={48}
                        alt={`Profile of ${formatName(trainer.name || 'Trainer')}`}
                      />
                      <div>
                        <h3 id={`trainer-${trainer.id}-title`} className="font-medium text-gray-900">
                          {formatName(trainer.name || 'Trainer')}
                        </h3>
                        <div className="flex items-center mt-1">
                          {renderStars(trainer.rating)}
                          <span className="text-xs text-gray-500 ml-1">{trainer.rating}</span>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4 mt-4">
                      {trainer.programs.map((program) => (
                        <div key={program.id} className="border-t border-gray-100 pt-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <h4 className="font-medium text-gray-800">{program.name}</h4>
                              <p className="text-sm text-gray-500">{program.description}</p>
                            </div>
                            {program.canReview && (
                              <button
                                onClick={() => {
                                  setCurrentReview({
                                    rating: 5,
                                    comment: '',
                                    trainerId: trainer.id,
                                    programId: program.id || program._id,
                                    planId: program.planId || null
                                  });
                                  setShowReviewModal(true);
                                }}
                                className={`${isPWA ? 'text-teal-700 hover:text-teal-800 bg-teal-50 hover:bg-teal-100 border border-teal-200/50' : 'text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100'} text-sm flex items-center px-3 py-1 rounded-lg transition-colors ${isPWA ? 'font-semibold shadow-sm' : ''}`}
                                aria-label={`Write review for ${program.name} with ${trainer.name}`}
                              >
                                <FiStar className="mr-1" /> Write Review
                              </button>
                            )}
                          </div>
                          {program.name?.includes('Consultation') ? (
                            <div className="mt-2 flex items-center text-sm text-gray-600">
                              <FiClock className="mr-2 text-gray-400" aria-hidden="true" />
                              Scheduled: {program.schedule}
                            </div>
                          ) : (
                            <>
                              {nextSessions[program.id || program._id] && (
                                <div className="mt-2 flex items-center text-sm text-gray-600">
                                  <FiClock className="mr-2 text-gray-400" aria-hidden="true" />
                                  Next: {nextSessions[program.id || program._id].formattedDate} at {nextSessions[program.id || program._id].formattedTime}
                                </div>
                              )}
                              {trainerSchedules[program.id || program._id] && (
                                <div className="mt-1 flex items-center text-sm text-gray-600">
                                  <FiCalendar className="mr-2 text-gray-400" aria-hidden="true" />
                                  {Object.keys(trainerSchedules[program.id || program._id].schedule || {}).filter(day =>
                                    trainerSchedules[program.id || program._id].schedule[day]?.length > 0
                                  ).join(', ') || 'Schedule not available'}
                                </div>
                              )}
                            </>
                          )}
                          <div className={`mt-3 flex ${isPWA ? 'flex-col gap-2' : 'space-x-2'}`}>
                            {program.name?.includes('Consultation') && program.isExpired ? (
                              <span className={`inline-flex items-center justify-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-full bg-gray-100 text-gray-500`}>
                                Session Ended
                              </span>
                            ) : (
                              <>
                                <a
                                  href={program.whatsappLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={`inline-flex items-center justify-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-full ${isPWA ? 'shadow-lg ring-2 ring-green-300/50 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600' : 'shadow-sm text-white bg-green-600 hover:bg-green-700'} transition-all ${isPWA ? 'text-white font-semibold' : ''}`}
                                  aria-label={`Join ${program.name} class on WhatsApp`}
                                >
                                  <FaWhatsapp className="mr-1.5" aria-hidden="true" /> {program.name?.includes('Consultation') ? 'Join Session' : 'Join Class'}
                                </a>
                                <button
                                  onClick={() => {
                                    setSelectedProgram(program);
                                    setShowCalendarModal(true);
                                  }}
                                  className={`inline-flex items-center justify-center px-3 py-1.5 border text-xs font-medium rounded-full ${isPWA ? 'border-teal-300/50 text-teal-700 bg-white/95 backdrop-blur-sm shadow-lg hover:bg-teal-50 ring-2 ring-teal-200/50 font-semibold' : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50 shadow-sm'} transition-all`}
                                  aria-label={`View schedule for ${program.name}`}
                                >
                                  <FaRegCalendarAlt className="mr-1.5" aria-hidden="true" /> Schedule
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className={`mt-6 ${isPWA ? 'space-y-3' : 'space-y-2'}`}>
                      <button
                        onClick={() => setSelectedTrainer(selectedTrainer?.id === trainer.id ? null : trainer)}
                        className={`w-full flex justify-between items-center px-4 py-2 ${isPWA ? 'bg-white/95 backdrop-blur-sm border-2 border-teal-200/50 text-teal-700 hover:bg-teal-50 shadow-lg hover:shadow-xl font-semibold' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'} rounded-lg transition-all`}
                        aria-expanded={selectedTrainer?.id === trainer.id}
                        aria-controls={`trainer-profile-${trainer.id}`}
                      >
                        <span>{selectedTrainer?.id === trainer.id ? 'Hide Profile' : 'View Mentor Profile'}</span>
                        {selectedTrainer?.id === trainer.id ? <FiChevronUp /> : <FiChevronDown />}
                      </button>
                      <button
                        onClick={() => {
                          setSelectedTrainer(trainer);
                          setShowMessageModal(true);
                        }}
                        className={`w-full flex justify-between items-center px-4 py-2 ${isPWA ? 'bg-gradient-to-r from-teal-500 to-blue-500 text-white shadow-lg ring-2 ring-teal-300/50 hover:from-teal-600 hover:to-blue-600 font-semibold' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'} rounded-lg transition-all`}
                        aria-label={`Message ${trainer.name}`}
                      >
                        <span>Message Mentor</span>
                        {trainer.unreadMessages > 0 && (
                          <span className={`${isPWA ? 'bg-white/30 text-white' : 'bg-blue-600 text-white'} text-xs font-bold px-2 py-0.5 rounded-full`}>
                            {trainer.unreadMessages}
                          </span>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Trainer Profile View */}
        {selectedTrainer && (
          <div
            ref={profileRef}
            id={`trainer-profile-${selectedTrainer.id}`}
            className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 mb-8 transition-all duration-300"
            aria-labelledby={`trainer-profile-title-${selectedTrainer.id}`}
          >
            <div className="p-8">
              <div className="flex justify-between items-start mb-6">
                <button
                  onClick={() => setSelectedTrainer(null)}
                  className="text-blue-600 hover:text-blue-800 flex items-center transition-colors"
                  aria-label="Close trainer profile"
                >
                  <FiX className="mr-1" aria-hidden="true" /> Close Profile
                </button>
                <button
                  onClick={() => setShowMessageModal(true)}
                  className="text-blue-600 hover:text-blue-800 flex items-center transition-colors"
                  aria-label={`Message ${formatName(selectedTrainer.name)}`}
                >
                  <FaEnvelope className="mr-1" aria-hidden="true" />
                  {selectedTrainer.unreadMessages > 0 ? (
                    <span className="ml-1">Messages ({selectedTrainer.unreadMessages})</span>
                  ) : (
                    <span className="ml-1">Message Trainer</span>
                  )}
                </button>
              </div>

              <div className="flex flex-col md:flex-row gap-8">
                <div className="md:w-1/3">
                  <div className="relative mb-6">
                    <div className="relative rounded-lg overflow-hidden shadow-md bg-gray-100">
                      <div className="w-full aspect-video flex items-center justify-center bg-gray-100">
                        <Avatar
                          src={(selectedTrainer.image && selectedTrainer.image.trim()) || selectedTrainer.profileImage}
                          name={selectedTrainer.name}
                          size={200}
                          alt={formatName(selectedTrainer.name)}
                        />
                      </div>
                      <div className="absolute bottom-4 left-4 bg-white px-3 py-1.5 rounded-full shadow-lg flex items-center border border-gray-200 z-10">
                        {renderStars(selectedTrainer.rating || 0, false, 'sm')}
                        <span className="ml-1 text-sm font-medium">{selectedTrainer.rating || 0}</span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-6">
                    <h2 id={`trainer-profile-title-${selectedTrainer.id}`} className="text-2xl font-bold text-gray-900">
                      {formatName(selectedTrainer.name)}
                    </h2>
                    <div className="mt-4 space-y-3">
                      <div className="flex items-center text-gray-600">
                        <FiUser className="mr-2 text-gray-500" aria-hidden="true" />
                        <span>{(selectedTrainer.yearsExperience || 0)}+ years experience</span>
                      </div>
                      <div className="flex items-center text-gray-600">
                        <RiMedalLine className="mr-2 text-gray-500" aria-hidden="true" />
                        <span>{(selectedTrainer.certifications || []).length} certifications</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="md:w-2/3">
                  <div className="mb-8">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">About</h3>
                    <p className="text-gray-700 leading-relaxed">{selectedTrainer.bio || 'No bio available'}</p>
                  </div>

                  <div className="mb-8">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Certifications</h3>
                    {(selectedTrainer.certifications || []).length > 0 ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {(selectedTrainer.certifications || []).map((cert, index) => (
                          <div
                            key={index}
                            className="bg-gray-50 p-3 rounded-lg border border-gray-100 hover:bg-gray-100 transition-colors"
                            aria-label={`Certification: ${cert}`}
                          >
                            <div className="flex items-center">
                              <RiMedalLine className="text-blue-500 mr-2" aria-hidden="true" />
                              <span className="font-medium">{cert}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">No certifications listed</p>
                    )}
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-xl font-semibold text-gray-900">Member Reviews</h3>
                      <span className="text-sm text-gray-500">{(selectedTrainer.reviews || []).length} reviews</span>
                    </div>
                    {(selectedTrainer.reviews || []).length > 0 ? (
                      <div className="space-y-4">
                        {(selectedTrainer.reviews || []).map((review) => (
                          <div key={review.id} className="border-b border-gray-200 pb-4 last:border-0">
                            <div className="flex justify-between items-start">
                              <div className="flex items-center space-x-3">
                                {(() => {
                                  // Filter out default/placeholder images to show initials instead
                                  const imageSrc = review.userImage || review.studentImage;
                                  const isDefaultImage = imageSrc && (
                                    imageSrc.includes('/src/images/avatar') ||
                                    imageSrc.includes('/images/avatar') ||
                                    imageSrc.includes('avatar.webp') ||
                                    imageSrc.includes('avatar-fem.jpg') ||
                                    imageSrc.includes('default-profile') ||
                                    imageSrc.includes('avatar')
                                  );
                                  const finalImageSrc = (imageSrc && !isDefaultImage) ? imageSrc : null;
                                  const studentName = review.userName === 'You'
                                    ? (review.studentName || session?.name || 'You')
                                    : (review.userName || review.studentName || 'User');

                                  return (
                                    <Avatar
                                      src={finalImageSrc}
                                      name={studentName}
                                      size={40}
                                      alt={`Profile of ${review.userName || review.studentName || 'User'}`}
                                    />
                                  );
                                })()}
                                <div>
                                  <h4 className="font-medium">{review.userName || review.studentName || 'User'}</h4>
                                  <div className="flex items-center">
                                    {renderStars(review.rating, false, 'sm')}
                                    <span className="text-xs text-gray-500 ml-2">{review.date}</span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex space-x-2 items-center">
                                {review.userName === 'You' && (
                                  <>
                                    <button
                                      onClick={() => handleEditReview(review)}
                                      className="text-gray-400 hover:text-blue-500 transition-colors"
                                      aria-label="Edit your review"
                                    >
                                      <FiEdit2 size={16} aria-hidden="true" />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteReview(selectedTrainer.id, review.id)}
                                      className="text-gray-400 hover:text-red-500 transition-colors"
                                      aria-label="Delete your review"
                                    >
                                      <FiTrash2 size={16} aria-hidden="true" />
                                    </button>
                                  </>
                                )}
                                <button
                                  onClick={() => handleLikeReview(selectedTrainer.id, review.id)}
                                  className={`flex items-center space-x-1 ${review.liked ? 'text-blue-500' : 'text-gray-400 hover:text-gray-600'} transition-colors`}
                                  aria-label={review.liked ? 'Unlike this review' : 'Like this review'}
                                >
                                  {review.liked ? <FaThumbsUp /> : <FaRegThumbsUp />}
                                  <span className="text-xs">{Array.isArray(review.likes) ? review.likes.length : (review.likes || 0)}</span>
                                </button>
                              </div>
                            </div>
                            <p className="mt-2 text-gray-700">{review.comment}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="bg-gray-50 rounded-lg p-6 text-center">
                        <p className="text-gray-500">No reviews yet. Be the first to review!</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Review Modal */}
      {showReviewModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="review-modal-title"
        >
          <div className="bg-white rounded-xl shadow-lg max-w-md w-full animate-fade-in">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 id="review-modal-title" className="text-2xl font-bold text-gray-900">
                  {editingReview ? 'Edit Your Review' : 'Rate Your Experience'}
                </h3>
                <button
                  onClick={() => {
                    setShowReviewModal(false);
                    setEditingReview(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label="Close review modal"
                >
                  <FiX size={24} aria-hidden="true" />
                </button>
              </div>

              <div className="mb-6">
                <label id="rating-label" className="block text-sm font-medium text-gray-700 mb-2">
                  Your Rating
                </label>
                <div
                  className="flex items-center space-x-1"
                  role="radiogroup"
                  aria-labelledby="rating-label"
                >
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setCurrentReview({ ...currentReview, rating: star })}
                      className="focus:outline-none transform hover:scale-110 transition-transform"
                      role="radio"
                      aria-checked={star === currentReview.rating}
                      aria-label={`${star} star${star !== 1 ? 's' : ''}`}
                    >
                      <FiStar
                        className={`h-8 w-8 ${star <= currentReview.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
                        aria-hidden="true"
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="comment" className="block text-sm font-medium text-gray-700 mb-2">
                  Your Review
                </label>
                <textarea
                  id="comment"
                  rows={4}
                  className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md p-3 border transition-colors"
                  placeholder="Share your experience with this trainer..."
                  value={currentReview.comment}
                  onChange={(e) => setCurrentReview({ ...currentReview, comment: e.target.value })}
                  aria-labelledby="comment-label"
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowReviewModal(false);
                    setEditingReview(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  aria-label="Cancel review"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReviewSubmit}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                  disabled={!currentReview.rating}
                  aria-label={editingReview ? 'Update review' : 'Submit review'}
                >
                  <FaRegComment className="mr-2" aria-hidden="true" />
                  {editingReview ? 'Update Review' : 'Submit Review'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Calendar Modal */}
      {showCalendarModal && selectedProgram && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="calendar-modal-title"
        >
          <div className="bg-white rounded-xl shadow-lg max-w-md w-full animate-fade-in">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 id="calendar-modal-title" className="text-2xl font-bold text-gray-900">
                  {selectedProgram.name} Schedule
                </h3>
                <button
                  onClick={() => setShowCalendarModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label="Close calendar modal"
                >
                  <FiX size={24} aria-hidden="true" />
                </button>
              </div>

              <div className="mb-4">
                {selectedProgram.name?.includes('Consultation') ? (
                  <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm font-medium text-gray-900 mb-1">Appointment Schedule:</p>
                    <p className="text-gray-700">{selectedProgram.schedule || 'Schedule not available'}</p>
                  </div>
                ) : (
                  <>
                    {trainerSchedules[selectedProgram.id || selectedProgram._id] && (
                      <div className="mb-4">
                        <h4 className="font-medium text-gray-900 mb-2">Weekly Schedule</h4>
                        {Object.entries(trainerSchedules[selectedProgram.id || selectedProgram._id].schedule || {}).map(([day, classes]) => {
                          if (classes.length === 0) return null;
                          return (
                            <div key={day} className="mb-2">
                              <span className="font-medium text-gray-700">{day}:</span>
                              {classes.map((cls, idx) => (
                                <span key={idx} className="ml-2 text-gray-600">
                                  {cls.classType} at {cls.time}
                                </span>
                              ))}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {nextSessions[selectedProgram.id || selectedProgram._id] && (
                      <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                        <p className="text-sm font-medium text-gray-900 mb-1">Next Session:</p>
                        <p className="text-gray-700">
                          {nextSessions[selectedProgram.id || selectedProgram._id].formattedDate} at {nextSessions[selectedProgram.id || selectedProgram._id].formattedTime}
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowCalendarModal(false)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  aria-label="Close calendar"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Message Modal */}
      {showMessageModal && selectedTrainer && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="message-modal-title"
        >
          <div className="bg-white rounded-xl shadow-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto animate-fade-in">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 id="message-modal-title" className="text-2xl font-bold text-gray-900">
                  Message {formatName(selectedTrainer.name)}
                </h3>
                <button
                  onClick={() => {
                    setShowMessageModal(false);
                    setShowMessageThread(null);
                    setMessageThread([]);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label="Close message modal"
                >
                  <FiX size={24} aria-hidden="true" />
                </button>
              </div>

              {/* Message Thread */}
              {showMessageThread && messageThread.length > 0 && (
                <div className="mb-6 border-b border-gray-200 pb-6">
                  <h4 className="font-medium text-gray-900 mb-4">Conversation</h4>
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {messageThread.map((msg) => (
                      <div
                        key={msg._id || msg.id}
                        className={`p-3 rounded-lg ${msg.isReply
                          ? 'bg-blue-50 ml-8'
                          : 'bg-gray-50'
                          }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-gray-700">
                            {msg.isReply ? formatName(selectedTrainer.name) : 'You'}
                          </span>
                          <span className="text-xs text-gray-500">
                            {formatDate(msg.createdAt || msg.date)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700">{msg.content || msg.message}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Send New Message */}
              <div className="mb-6">
                <label htmlFor="message-content" className="block text-sm font-medium text-gray-700 mb-2">
                  Your Message
                </label>
                <textarea
                  id="message-content"
                  ref={messageInputRef}
                  rows={4}
                  className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md p-3 border transition-colors"
                  placeholder={`Write your message to ${formatName(selectedTrainer.name)}...`}
                  value={messageContent}
                  onChange={(e) => setMessageContent(e.target.value)}
                  aria-labelledby="message-content-label"
                />
              </div>

              <div className="flex justify-between items-center">
                {/* View Thread Button */}
                {studentMessages.filter(m => m.trainerId?.toString() === selectedTrainer.id?.toString()).length > 0 && (
                  <button
                    onClick={() => {
                      const firstMessage = studentMessages.find(m => m.trainerId?.toString() === selectedTrainer.id?.toString());
                      if (firstMessage?.threadId) {
                        loadMessageThread(firstMessage.threadId);
                      }
                    }}
                    className="px-4 py-2 text-blue-600 hover:text-blue-800 text-sm"
                  >
                    {showMessageThread ? 'Hide' : 'View'} Conversation
                  </button>
                )}
                <div className="flex justify-end space-x-3 ml-auto">
                  <button
                    onClick={() => {
                      setShowMessageModal(false);
                      setShowMessageThread(null);
                      setMessageThread([]);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                    aria-label="Cancel message"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      await handleSendMessage();
                      // Refresh messages after sending
                      const response = await HttpInterceptor.get('/api/messages/student');
                      if (response.status === 200) {
                        setStudentMessages(response.data?.data || []);
                        // Reload thread if open
                        if (showMessageThread) {
                          const firstMessage = studentMessages.find(m => m.trainerId?.toString() === selectedTrainer.id?.toString());
                          if (firstMessage?.threadId) {
                            loadMessageThread(firstMessage.threadId);
                          }
                        }
                      }
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                    disabled={!messageContent.trim()}
                    aria-label="Send message"
                  >
                    <FaEnvelope className="mr-2" aria-hidden="true" />
                    Send Message
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Message Thread Modal */}
      {showMessageThread && !showMessageModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white rounded-xl shadow-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-2xl font-bold text-gray-900">Conversation</h3>
                <button
                  onClick={() => {
                    setShowMessageThread(null);
                    setMessageThread([]);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <FiX size={24} />
                </button>
              </div>
              {isLoadingMessages ? (
                <div className="text-center py-8">
                  <FaSpinner className="animate-spin text-blue-600 text-2xl mx-auto" />
                </div>
              ) : messageThread.length > 0 ? (
                <div className="space-y-3">
                  {messageThread.map((msg) => (
                    <div
                      key={msg._id || msg.id}
                      className={`p-3 rounded-lg ${msg.isReply
                        ? 'bg-blue-50 ml-8'
                        : 'bg-gray-50'
                        }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-gray-700">
                          {msg.isReply ? 'Trainer' : 'You'}
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatDate(msg.createdAt || msg.date)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700">{msg.content || msg.message}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-8">No messages in this conversation</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Unified Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmConfig.isOpen}
        onClose={() => setConfirmConfig({ ...confirmConfig, isOpen: false })}
        onConfirm={confirmConfig.onConfirm}
        title={confirmConfig.title}
        message={confirmConfig.message}
        variant={confirmConfig.variant}
      />
    </div>
  );
}

export default MyTrainer;